/**
 * InvoicePDFPreview Component
 * Wrapper that decides whether to use desktop or mobile PDF preview
 * - Desktop: PDFPreview (uses @react-pdf/renderer PDFViewer)
 * - Mobile: MobilePDFPreview (uses react-pdf canvas rendering)
 */

import { useState, useEffect } from 'react';
import { PDFPreview } from './PDFPreview';
import { MobilePDFPreview } from './MobilePDFPreview';
import type { Invoice } from '@shared/schemas/invoice.schema';
import type { PaperSize } from '@/types/paper';
import type { PageLayout } from '@/utils/pagination';

interface InvoicePDFPreviewProps {
  invoice: Invoice;
  paperSize: PaperSize;
  pageLayouts: PageLayout[];
}

export function InvoicePDFPreview({ invoice, paperSize, pageLayouts }: InvoicePDFPreviewProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    setIsMobile(mobile);
  }, []);

  // Mobile: Use canvas-based rendering
  if (isMobile) {
    return <MobilePDFPreview invoice={invoice} paperSize={paperSize} pageLayouts={pageLayouts} />;
  }

  // Desktop: Use PDFViewer
  return <PDFPreview invoice={invoice} paperSize={paperSize} pageLayouts={pageLayouts} />;
}
